import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Film,
  History,
  LayoutTemplate,
  Loader2,
  MessageSquarePlus,
  Paperclip,
  RefreshCw,
  SlidersHorizontal,
  Sparkles,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { TfxRenderJobDTO } from '@/api/generated/model';
import { ROUTES } from '@/app/routes';
import {
  MAX_JOB_OPTIONS_LEN,
  loadJobSpec,
  sanitizeRefName,
  useCreatePromptJob,
  useMyTfxJobs,
  uploadPromptRef,
  type TfxSpec,
  type TfxSpecParam,
} from '../api';
import { TfxJobStatusBadge } from '../components/TfxStatusBadge';
import { formatModifiedAt } from '../lib/display';
import {
  buildFootageRefineOptions,
  capFootageRefName,
  composeFootageSource,
  parseFootageJobOptions,
  previewHintFromSource,
} from '../lib/footageCompose';

// Mirror the backend budgets (TfxValidation.MAX_PROMPT_REFS / MAX_PROMPT_LEN).
const MAX_REFS = 4;
const MAX_PROMPT_LEN = 4000;
const ACCEPT = 'image/png,image/jpeg,image/webp,video/mp4,video/quicktime';

/**
 * Self-serve footage channel (§2/§5) — closed sets mirrored from
 * TfxValidation.FOOTAGE_BLENDS / FOOTAGE_PLACEMENTS, the same way the prompt
 * budgets above mirror theirs. The worker re-validates all of it; this exists
 * so a bad pick fails before an upload, not after a render.
 */
const FOOTAGE_BLENDS = [
  { id: 'screen', label: 'Screen — glows on a black background' },
  { id: 'add', label: 'Add — brighter, blows out sooner' },
  { id: 'over', label: 'Over — the clip must carry real alpha' },
] as const;

const FOOTAGE_PLACEMENTS = [
  { id: 'full', label: 'Full frame' },
  { id: 'bottom', label: 'Bottom band' },
  { id: 'top', label: 'Top band' },
  { id: 'left', label: 'Left half' },
  { id: 'right', label: 'Right half' },
] as const;

const FOOTAGE_ACCEPT = 'video/mp4,video/quicktime';
// TfxValidation.MAX_LICENSE_PROVENANCE_LEN is 1000; the design caps the FE
// tighter so the whole options object stays well inside the 2048 budget.
const MAX_PROVENANCE_LEN = 600;
const MAX_SPDX_LEN = 64;
// An AI-composed footage job carries the effects description in `source`, with
// the preview-content hint appended on a trailing line; the API bounds the
// whole of `source` by MAX_PROMPT_LEN, so leave that line room.
const MAX_COMPOSE_LEN = MAX_PROMPT_LEN - 64;

const SELECT_CLASS =
  'flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm';

/**
 * Motion templates (Motion-Template §5.2) — the closed set the compiler
 * declares, mirrored here the same way the prompt budgets above are: the
 * worker's `tfx_compile.py` (kind "motion") stays the authority on ranges and
 * rejects anything out of band, this table only drives the form.
 */
type TemplateParam = { key: string; label: string; min: number; max: number; def: number };
type TemplatePreset = {
  id: string;
  label: string;
  blurb: string;
  chrome?: { id: string; label: string }[];
  params: TemplateParam[];
};

const TEMPLATE_PRESETS: TemplatePreset[] = [
  {
    id: 'card-3d',
    label: '3D card',
    blurb: 'Video flies into a social card, wobbles in 3D, zooms through at the end',
    chrome: [
      { id: 'social', label: 'Social chrome (frame + like row + heart)' },
      { id: 'none', label: 'Clean rounded card' },
    ],
    params: [
      { key: 'uDim', label: 'Backdrop dim', min: 0, max: 0.8, def: 0.35 },
      { key: 'uBlur', label: 'Backdrop blur', min: 0, max: 1, def: 0.6 },
      { key: 'uStrength', label: 'Zoom-through blur', min: 0, max: 1, def: 0.5 },
    ],
  },
  {
    id: 'camera-push',
    label: 'Camera push',
    blurb: 'Slow push-in on the whole frame with a radial motion blur',
    params: [{ key: 'uStrength', label: 'Motion blur', min: 0, max: 0.8, def: 0.25 }],
  },
  {
    id: 'shake-pulse',
    label: 'Shake pulse',
    blurb: 'Beat-style camera shake with a directional smear',
    params: [
      { key: 'uAmount', label: 'Shake', min: 0, max: 1, def: 0.5 },
      { key: 'uStrength', label: 'Smear', min: 0, max: 0.5, def: 0.12 },
    ],
  },
];

/** Preview base clip the worker picks from the job source (keyword match). */
const PREVIEW_HINTS = ['portrait', 'food', 'city', 'nature'];

/** SPEC param-edit jobs carry a human label in options.editNote. */
function editNoteOf(job: TfxRenderJobDTO): string | null {
  if (!job.options) return null;
  try {
    const note = (JSON.parse(job.options) as { editNote?: unknown }).editNote;
    return typeof note === 'string' ? note : null;
  } catch {
    return null;
  }
}

/** TEMPLATE jobs are described by their RECIPE, not by a prompt (the same
 *  line the worker writes onto the reviewer's draft card). */
function templateText(job: TfxRenderJobDTO): string | null {
  if (!job.options) return null;
  try {
    const o = JSON.parse(job.options) as {
      preset?: string;
      chrome?: string;
      params?: Record<string, number>;
    };
    if (!o.preset) return null;
    const preset = TEMPLATE_PRESETS.find((p) => p.id === o.preset);
    const bits = [`Template: ${preset?.label ?? o.preset}`];
    if (o.chrome) bits.push(o.chrome);
    const params = o.params ?? {};
    const tuned = Object.keys(params).sort();
    if (tuned.length > 0) bits.push(tuned.map((k) => `${k}=${params[k]}`).join(', '));
    return bits.join(' · ');
  } catch {
    return null;
  }
}

/** One-line description of a job inside its chain. */
/**
 * A footage card is judged on its RECIPE and its RIGHTS, not on a prompt —
 * surface the blend, the placement and the licence the admin attested to. An
 * AI-composed job (compose "ai") also stacked effects around the clip, so its
 * description leads the line: that is the part an iteration changes.
 */
function footageText(job: TfxRenderJobDTO): string | null {
  if (!job.options) return null;
  try {
    const o = JSON.parse(job.options) as {
      name?: string;
      blend?: string;
      loop?: boolean;
      place?: { preset?: string };
      license?: { spdx?: string };
      compose?: string;
    };
    const bits = [`Footage: ${o.name ?? '—'}`];
    bits.push(
      `${o.blend ?? 'screen'}/${o.place?.preset ?? 'full'}${o.loop === false ? '/one-shot' : ''}`,
    );
    if (o.license?.spdx) bits.push(`licence ${o.license.spdx}`);
    if (o.compose === 'ai') {
      // The effects description is the first line of `source`; the trailing
      // "Preview template: x" line is the preview hint, not the recipe.
      const description = (job.source ?? '').split('\n')[0]?.trim();
      if (description) bits.push(`AI: ${description}`);
    }
    return bits.join(' · ');
  } catch {
    return null;
  }
}

function jobText(job: TfxRenderJobDTO): string {
  if (job.kind === 'SPEC') return editNoteOf(job) ?? 'parameter edit';
  if (job.kind === 'TEMPLATE') return templateText(job) ?? 'motion template';
  if (job.kind === 'FOOTAGE') return footageText(job) ?? 'uploaded footage';
  return job.source ?? '';
}

/**
 * The chain-root job id an iteration belongs to. Every generated effect id
 * ends with the ORIGINAL job's `-j<id>` uniquifier, and both iteration
 * channels carry that id along (SPEC jobs verbatim in `source`, refine jobs
 * in options.refine.parentSpec.id) — so the trailing number IS the root job.
 * Original PROMPT jobs return null and root their own chain.
 */
function rootJobIdOf(job: TfxRenderJobDTO): number | null {
  let effectId: string | null = null;
  if (job.kind === 'TEMPLATE') {
    // A re-tune pins its package id to the originating job via variantOf, so
    // every version of a template variant groups under one card.
    try {
      const v = (JSON.parse(job.options ?? '{}') as { variantOf?: unknown }).variantOf;
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    } catch {
      return null;
    }
  }
  if (job.kind === 'SPEC' && job.source) {
    try {
      effectId = (JSON.parse(job.source) as { id?: string }).id ?? null;
    } catch {
      effectId = null;
    }
  } else if (job.options) {
    try {
      const parsed = JSON.parse(job.options) as { refine?: { parentSpec?: { id?: string } } };
      effectId = parsed.refine?.parentSpec?.id ?? null;
    } catch {
      effectId = null;
    }
  }
  const match = effectId ? /-j(\d+)$/.exec(effectId) : null;
  return match ? Number(match[1]) : null;
}

function iterationChip(job: TfxRenderJobDTO): string | null {
  if (job.kind === 'SPEC') return 'param edit';
  if (job.kind === 'TEMPLATE') return 'template';
  return rootJobIdOf(job) !== null ? 'refine' : null;
}

/** One card per effect: the whole iteration chain (original → refines/edits). */
function EffectCard({ chain }: { chain: TfxRenderJobDTO[] }) {
  const root = chain[0]!;
  const latest = chain[chain.length - 1]!;
  const lastDone = [...chain].reverse().find((j) => j.status === 'DONE');
  const active = latest.status === 'QUEUED' || latest.status === 'RUNNING';
  const refCount = root.refs?.length ?? 0;

  // Every poll re-signs artifact URLs; pin the first URL per s3Key so <video>
  // src stays stable across refetches (presigned URLs stay valid for hours).
  const urlCache = useRef(new Map<string, string>());
  function movieUrlFor(job: TfxRenderJobDTO): string | undefined {
    const movie = job.artifacts?.find((a) => a.kind === 'movie');
    if (!movie?.s3Key) return undefined;
    const cached = urlCache.current.get(movie.s3Key);
    if (cached) return cached;
    if (movie.url) urlCache.current.set(movie.s3Key, movie.url);
    return movie.url;
  }

  const [shownId, setShownId] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const shown = chain.find((j) => j.id === shownId) ?? latest;
  const shownIndex = chain.indexOf(shown);
  const movieUrl = movieUrlFor(shown);

  const createJob = useCreatePromptJob();
  const [panel, setPanel] = useState<'refine' | 'adjust' | 'tune' | null>(null);
  const [feedback, setFeedback] = useState('');
  const [spec, setSpec] = useState<TfxSpec | null>(null);
  const [values, setValues] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);

  /**
   * TEMPLATE chains iterate through their OWN closed form, never the AI
   * refine / spec-edit panels: a motion spec's `params` is an object (not the
   * array those panels index), and the AI channel cannot author kind "motion"
   * at all. Everything the Tune panel needs already sits in the job options,
   * so it also never fetches the spec artifact.
   */
  const templateOptions = useMemo(() => {
    const source = [...chain].reverse().find((j) => j.kind === 'TEMPLATE' && j.options);
    if (!source?.options) return null;
    try {
      const o = JSON.parse(source.options) as {
        preset?: string;
        name?: string;
        chrome?: string;
        params?: Record<string, number>;
      };
      const preset = TEMPLATE_PRESETS.find((p) => p.id === o.preset);
      return preset ? { preset, name: o.name, chrome: o.chrome, params: o.params ?? {} } : null;
    } catch {
      return null;
    }
  }, [chain]);
  const isTemplate = root.kind === 'TEMPLATE';

  /**
   * An AI-composed footage root iterates through the SAME refine panel as a
   * prompt effect, but its job re-posts the whole footage payload: the worker
   * pins the clip, the blend, the placement and the licence from the original
   * form, and only the stacked effects are re-authored. A plain footage root
   * has nothing for the model to change, so it stays non-refinable.
   */
  const footageRoot = useMemo(
    () => (root.kind === 'FOOTAGE' ? parseFootageJobOptions(root.options) : null),
    [root.kind, root.options],
  );
  const canRefine =
    root.kind === 'FOOTAGE' ? footageRoot?.compose === 'ai' : root.kind !== 'TEMPLATE';
  /**
   * Adjust posts a SPEC job, which renders from the spec alone — it has no
   * sidecar rail, so a footage package would come back without its clip.
   * Footage params are re-tuned through Refine instead.
   */
  const canAdjust = canRefine && root.kind !== 'FOOTAGE';

  const floatParams = (Array.isArray(spec?.params) ? spec.params : []).filter(
    (p): p is TfxSpecParam & { min: number; max: number } =>
      (p.type ?? 'float') === 'float' && typeof p.min === 'number' && typeof p.max === 'number',
  );

  async function openPanel(which: 'refine' | 'adjust' | 'tune') {
    if (panel === which) {
      setPanel(null);
      return;
    }
    if (!lastDone) return;
    if (which === 'tune') {
      if (!templateOptions) return;
      const init: Record<string, number> = {};
      for (const p of templateOptions.preset.params) {
        init[p.key] = templateOptions.params[p.key] ?? p.def;
      }
      setValues(init);
      setPanel('tune');
      return;
    }
    try {
      // Iterate from the newest DONE version's spec (latest params/kernel).
      const s = await loadJobSpec(lastDone);
      setSpec(s);
      const init: Record<string, number> = {};
      for (const p of Array.isArray(s.params) ? s.params : []) {
        if (typeof p.default === 'number') init[p.key] = p.default;
      }
      setValues(init);
      setPanel(which);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load the effect spec');
    }
  }

  async function submitTune() {
    if (!templateOptions) return;
    setBusy(true);
    try {
      const params: Record<string, number> = {};
      for (const p of templateOptions.preset.params) {
        const v = values[p.key];
        if (typeof v === 'number' && v !== p.def) params[p.key] = Number(v.toFixed(2));
      }
      await createJob.mutateAsync({
        data: {
          kind: 'TEMPLATE',
          source: root.source ?? 'portrait',
          options: JSON.stringify({
            preset: templateOptions.preset.id,
            ...(templateOptions.name ? { name: templateOptions.name } : {}),
            ...(templateOptions.chrome ? { chrome: templateOptions.chrome } : {}),
            ...(Object.keys(params).length > 0 ? { params } : {}),
            // Pins the package id to this chain, so the re-tune upserts the
            // SAME draft instead of forking a second effect.
            variantOf: root.id,
          }),
        },
      });
      setPanel(null);
      setShownId(null);
      toast.success('Re-tune queued — this card will update in place');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to queue the re-tune');
    } finally {
      setBusy(false);
    }
  }

  async function submitRefine() {
    const trimmed = feedback.trim();
    if (!trimmed || !spec) return;
    setBusy(true);
    try {
      // The chain root's prompt is the true original request (an iteration's
      // own source is only its feedback line).
      const originalPrompt = iterationChip(root) ? undefined : root.source;
      let data: { kind: string; source: string; options: string };
      if (footageRoot) {
        // A footage iteration re-posts the WHOLE payload: the clip, the blend,
        // the placement and the licence are the admin's, not the model's, and
        // the worker re-pins them from these options on every version.
        const options = buildFootageRefineOptions(
          footageRoot,
          spec,
          originalPrompt,
          MAX_JOB_OPTIONS_LEN,
        );
        if (!options) {
          // Name BOTH levers: the provenance is re-posted whole with every
          // version, and the parent spec grows with each stacked step and each
          // exposed param (~60 characters apiece).
          const steps = Array.isArray(spec.stack) ? spec.stack.length : 0;
          const params = Array.isArray(spec.params) ? spec.params.length : 0;
          throw new Error(
            `This footage effect does not fit the ${MAX_JOB_OPTIONS_LEN}-character job options budget: every version re-posts its ${footageRoot.license.provenance.length}-character licence provenance alongside the parent spec (${steps} stack steps, ${params} exposed params). Re-render the clip with a shorter provenance, or start a fresh effect with a smaller stack, to make it refinable.`,
          );
        }
        // Carry the parent's preview-content pick onto the iteration so the
        // worker resolves the same base clip for both versions (design §1.1:
        // choose_template has to honour this line for it to beat a keyword
        // that happens to sit in the feedback text).
        const previewHint = previewHintFromSource(root.source);
        data = {
          kind: 'FOOTAGE',
          source: previewHint ? composeFootageSource(trimmed, previewHint) : trimmed,
          options,
        };
      } else {
        const bare = { parentSpec: spec };
        const rootPrompt = root.kind === 'PROMPT' ? originalPrompt : undefined;
        const withPrompt = rootPrompt ? { ...bare, parentPrompt: rootPrompt } : bare;
        // Backend options budget is 2048: drop the original prompt first; the
        // parent spec itself is non-negotiable (it pins the effect id).
        let options = JSON.stringify({ refine: withPrompt });
        if (options.length > MAX_JOB_OPTIONS_LEN) options = JSON.stringify({ refine: bare });
        if (options.length > MAX_JOB_OPTIONS_LEN) {
          throw new Error('This spec is too large to refine from the console');
        }
        data = { kind: 'PROMPT', source: trimmed, options };
      }
      await createJob.mutateAsync({ data });
      setPanel(null);
      setFeedback('');
      setShownId(null);
      toast.success(`Refinement of ${spec.id} queued — this card will update in place`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to queue the refinement');
    } finally {
      setBusy(false);
    }
  }

  async function submitAdjust() {
    if (!spec) return;
    setBusy(true);
    try {
      const edited: TfxSpec = {
        ...spec,
        params: (spec.params ?? []).map((p) => {
          const v = values[p.key];
          return typeof v === 'number' ? { ...p, default: v } : p;
        }),
      };
      await createJob.mutateAsync({
        data: {
          kind: 'SPEC',
          source: JSON.stringify(edited, null, 2),
          options: JSON.stringify({ publishDraft: true, editNote: `param edit of ${spec.id}` }),
        },
      });
      setPanel(null);
      setShownId(null);
      toast.success(`Re-render of ${spec.id} queued — this card will update in place`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to queue the re-render');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="flex items-center gap-3">
          <TfxJobStatusBadge status={latest.status ?? '?'} />
          {chain.length > 1 && (
            <span className="rounded-md border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              v{chain.length}
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            #{root.id} · {formatModifiedAt(latest.createdAt)}
          </span>
          {refCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {refCount} reference{refCount > 1 ? 's' : ''}
            </span>
          )}
          {active && <Loader2 className="size-4 animate-spin text-primary" />}
          <div className="flex-1" />
          {chain.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => setHistoryOpen((v) => !v)}
            >
              <History className="size-4" />
              History ({chain.length})
            </Button>
          )}
        </div>
        <p className="whitespace-pre-wrap text-sm">{jobText(root)}</p>
        {chain.length > 1 && latest !== root && (
          <p className="text-xs text-muted-foreground">
            v{chain.length}: {jobText(latest)}
          </p>
        )}
        {historyOpen && (
          <div className="flex flex-col overflow-hidden rounded-md border border-border">
            {chain.map((j, i) => (
              <button
                key={j.id ?? i}
                type="button"
                onClick={() => setShownId(j.id ?? null)}
                className={`flex items-center gap-2 border-b border-border px-3 py-2 text-left text-xs last:border-b-0 hover:bg-muted/50 ${
                  shown.id === j.id ? 'bg-muted/60' : ''
                }`}
              >
                <span className="w-7 shrink-0 font-mono text-muted-foreground">v{i + 1}</span>
                <TfxJobStatusBadge status={j.status ?? '?'} />
                {iterationChip(j) && (
                  <span className="shrink-0 rounded-md border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                    {iterationChip(j)}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate">{jobText(j)}</span>
                <span className="shrink-0 font-mono text-muted-foreground">#{j.id}</span>
              </button>
            ))}
          </div>
        )}
        {latest.status === 'FAILED' && latest.error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <span className="whitespace-pre-wrap break-all">{latest.error}</span>
          </div>
        )}
        {movieUrl && (
          <div className="flex flex-col gap-1">
            {shown !== latest && (
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                viewing v{shownIndex + 1} of {chain.length}
              </span>
            )}
            <video
              key={movieUrl}
              src={movieUrl}
              controls
              loop
              muted
              playsInline
              className="max-h-72 w-fit rounded-md border border-border"
            />
          </div>
        )}
        {lastDone && isTemplate && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void openPanel('tune')}
              disabled={busy || !templateOptions}
            >
              <SlidersHorizontal className="size-4" />
              Tune template
            </Button>
            {!templateOptions && (
              <span className="text-xs text-muted-foreground">
                This variant was queued with options this console cannot re-open.
              </span>
            )}
          </div>
        )}
        {lastDone && canRefine && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void openPanel('refine')}
              disabled={busy}
            >
              <MessageSquarePlus className="size-4" />
              Refine
            </Button>
            {canAdjust && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void openPanel('adjust')}
                disabled={busy}
              >
                <SlidersHorizontal className="size-4" />
                Adjust params
              </Button>
            )}
          </div>
        )}
        {panel === 'tune' && templateOptions && (
          <div className="flex flex-col gap-3 rounded-md border border-border p-3">
            <span className="text-xs text-muted-foreground">
              {templateOptions.preset.label}
              {templateOptions.chrome ? ` · ${templateOptions.chrome}` : ''} — re-renders this same
              effect with new values.
            </span>
            {templateOptions.preset.params.map((p) => (
              <label key={p.key} className="flex items-center gap-3 text-xs">
                <span className="w-32 shrink-0">{p.label}</span>
                <input
                  type="range"
                  min={p.min}
                  max={p.max}
                  step={0.01}
                  value={values[p.key] ?? p.def}
                  onChange={(e) => setValues({ ...values, [p.key]: Number(e.target.value) })}
                  className="flex-1 accent-primary"
                  disabled={busy}
                />
                <span className="w-10 text-right font-mono tabular-nums">
                  {(values[p.key] ?? p.def).toFixed(2)}
                </span>
              </label>
            ))}
            <div className="flex justify-end">
              <Button size="sm" disabled={busy} onClick={() => void submitTune()}>
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <SlidersHorizontal className="size-4" />
                )}
                Re-render
              </Button>
            </div>
          </div>
        )}
        {panel === 'refine' && (
          <div className="flex flex-col gap-2 rounded-md border border-border p-3">
            {footageRoot && (
              <span className="text-xs text-muted-foreground">
                Your clip, its blend, its placement and its licence stay exactly as uploaded — only
                the effects stacked around it are re-composed.
              </span>
            )}
            <Textarea
              rows={2}
              // A footage refine appends the preview-template line to `source`,
              // which the API bounds by MAX_PROMPT_LEN as a whole.
              maxLength={footageRoot ? MAX_COMPOSE_LEN : MAX_PROMPT_LEN}
              placeholder={
                footageRoot
                  ? 'What should change? e.g. "shake harder, and warm the grade before the clip"'
                  : 'What should change? e.g. "stronger glitch, more retro color bleed"'
              }
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              disabled={busy}
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                disabled={busy || !feedback.trim()}
                onClick={() => void submitRefine()}
              >
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Sparkles className="size-4" />
                )}
                Generate new version
              </Button>
            </div>
          </div>
        )}
        {panel === 'adjust' && spec && (
          <div className="flex flex-col gap-3 rounded-md border border-border p-3">
            {floatParams.length === 0 ? (
              <span className="text-xs text-muted-foreground">
                This effect exposes no adjustable parameters — use Refine instead.
              </span>
            ) : (
              floatParams.map((p) => (
                <label key={p.key} className="flex items-center gap-3 text-xs">
                  <span className="w-24 shrink-0 font-mono">{p.key}</span>
                  <input
                    type="range"
                    min={p.min}
                    max={p.max}
                    step={0.01}
                    value={values[p.key] ?? p.default ?? p.min}
                    onChange={(e) => setValues({ ...values, [p.key]: Number(e.target.value) })}
                    className="flex-1 accent-primary"
                    disabled={busy}
                  />
                  <span className="w-10 text-right font-mono tabular-nums">
                    {(values[p.key] ?? p.default ?? p.min).toFixed(2)}
                  </span>
                </label>
              ))
            )}
            <div className="flex justify-end">
              <Button
                size="sm"
                disabled={busy || floatParams.length === 0}
                onClick={() => void submitAdjust()}
              >
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <SlidersHorizontal className="size-4" />
                )}
                Re-render
              </Button>
            </div>
          </div>
        )}
        {latest.status === 'DONE' && (
          <p className="text-xs text-muted-foreground">
            Draft generated and submitted for review — approve or reject it in the{' '}
            <Link to={ROUTES.tfxReview} className="text-primary underline-offset-2 hover:underline">
              Review
            </Link>{' '}
            tab.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * AI effect creation (kind PROMPT render jobs): describe the effect, optionally
 * attach image/video style references, and the Mac mini worker generates the
 * spec with the claude CLI, renders a preview and submits a draft for review.
 * Iterations (Refine / Adjust params) update the SAME draft, so the job list
 * is grouped into one card per effect: the newest version fronts the card and
 * older versions sit behind the History toggle.
 */
export function TfxCreatePage() {
  const [mode, setMode] = useState<'prompt' | 'template' | 'footage'>('prompt');
  const [prompt, setPrompt] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Template mode: preset + chrome variant + tuned params + preview content.
  const [presetId, setPresetId] = useState(TEMPLATE_PRESETS[0]!.id);
  const [chrome, setChrome] = useState(TEMPLATE_PRESETS[0]!.chrome?.[0]?.id ?? '');
  const [variantName, setVariantName] = useState('');
  const [tuned, setTuned] = useState<Record<string, number>>({});
  const [previewHint, setPreviewHint] = useState(PREVIEW_HINTS[0]!);

  // Footage mode: the admin's own clip + a closed form + the attestation.
  const [clip, setClip] = useState<File | null>(null);
  const [footageName, setFootageName] = useState('');
  const [blend, setBlend] = useState<string>(FOOTAGE_BLENDS[0].id);
  const [placement, setPlacement] = useState<string>(FOOTAGE_PLACEMENTS[0].id);
  const [loop, setLoop] = useState(true);
  const [spdx, setSpdx] = useState('');
  const [provenance, setProvenance] = useState('');
  const [rightsOwned, setRightsOwned] = useState(false);
  // Empty = publish the clip as-is (compose "plain"); filled = the model
  // stacks the described effects around the clip (compose "ai").
  const [composeText, setComposeText] = useState('');
  const footageNameValid = /^[a-z0-9][a-z0-9-]{1,30}$/.test(footageName);
  // Every licence field AND the explicit rights checkbox: this channel
  // publishes someone's actual footage to a public CDN.
  const footageReady =
    clip !== null &&
    footageNameValid &&
    spdx.trim() !== '' &&
    provenance.trim() !== '' &&
    rightsOwned;
  const preset = TEMPLATE_PRESETS.find((p) => p.id === presetId) ?? TEMPLATE_PRESETS[0]!;
  const nameValid = variantName === '' || /^[a-z0-9][a-z0-9-]{1,30}$/.test(variantName);

  function selectPreset(id: string) {
    const next = TEMPLATE_PRESETS.find((p) => p.id === id) ?? TEMPLATE_PRESETS[0]!;
    setPresetId(next.id);
    setChrome(next.chrome?.[0]?.id ?? '');
    setTuned({});
  }

  async function onSubmitTemplate() {
    if (!nameValid) return;
    setSubmitting(true);
    try {
      // Only CHANGED params ride the options: the compiler owns the defaults,
      // and the column budget is 2048.
      const params: Record<string, number> = {};
      for (const p of preset.params) {
        const v = tuned[p.key];
        if (typeof v === 'number' && v !== p.def) params[p.key] = Number(v.toFixed(2));
      }
      await createJob.mutateAsync({
        data: {
          kind: 'TEMPLATE',
          source: previewHint,
          options: JSON.stringify({
            preset: preset.id,
            ...(variantName ? { name: variantName } : {}),
            ...(preset.chrome ? { chrome } : {}),
            ...(Object.keys(params).length > 0 ? { params } : {}),
          }),
        },
      });
      setVariantName('');
      setTuned({});
      toast.success('Template variant queued — the render worker picks it up shortly');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to queue the template job');
    } finally {
      setSubmitting(false);
    }
  }
  async function onSubmitFootage() {
    if (!footageReady || !clip) return;
    setSubmitting(true);
    try {
      // Resolve the name BEFORE uploading: an unsupported container must fail
      // the submit, not leave an orphaned object in the bucket. The extra cap
      // is this channel's: the key rides every refine's options alongside the
      // licence and the parent spec, and a 100-character basename spends that
      // budget on the file the admin happened to drag in.
      const refKey = await uploadPromptRef(clip, capFootageRefName(sanitizeRefName(clip)));
      // A description turns this into the AI-composed channel: the model
      // stacks effects around the clip. Empty keeps today's payload exactly —
      // clip as-is, no `compose` key (the worker defaults to plain).
      const description = composeText.trim();
      await createJob.mutateAsync({
        data: {
          kind: 'FOOTAGE',
          // Plain: `source` is the preview-content hint, same as TEMPLATE.
          // AI: the effects description, with that hint on a trailing line.
          source: description ? composeFootageSource(description, previewHint) : previewHint,
          options: JSON.stringify({
            refs: [refKey],
            name: footageName,
            blend,
            loop,
            place: { preset: placement },
            license: { spdx: spdx.trim(), provenance: provenance.trim() },
            ...(description ? { compose: 'ai' } : {}),
          }),
        },
      });
      setClip(null);
      setFootageName('');
      setProvenance('');
      setRightsOwned(false);
      setComposeText('');
      toast.success(
        description
          ? 'Footage effect queued — the worker composes the stack and renders a preview shortly'
          : 'Footage effect queued — the render worker picks it up shortly',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to queue the footage job');
    } finally {
      setSubmitting(false);
    }
  }

  const { data, isLoading, isFetching, isError, error, refetch } = useMyTfxJobs();
  const createJob = useCreatePromptJob();
  const jobs = data ?? [];

  // Group the flat newest-first job list into per-effect chains (oldest-first
  // inside a chain), ordered by their latest activity. A chain whose root job
  // fell outside the list window still groups under the root id.
  const chains = useMemo(() => {
    const byRoot = new Map<number, TfxRenderJobDTO[]>();
    const ascending = [...jobs].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    for (const job of ascending) {
      const key = rootJobIdOf(job) ?? job.id ?? -1;
      const chain = byRoot.get(key);
      if (chain) chain.push(job);
      else byRoot.set(key, [job]);
    }
    return [...byRoot.values()].sort(
      (a, b) => (b[b.length - 1]!.id ?? 0) - (a[a.length - 1]!.id ?? 0),
    );
  }, [jobs]);

  function addFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    const merged = [...files, ...Array.from(list)];
    if (merged.length > MAX_REFS) toast.error(`At most ${MAX_REFS} reference files`);
    setFiles(merged.slice(0, MAX_REFS));
  }

  async function onSubmit() {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      // Resolve every filename first: an unsupported file must fail the whole
      // submit BEFORE any sibling has been uploaded (no orphaned refs).
      const names = files.map((file) => sanitizeRefName(file));
      const refs: string[] = [];
      for (const [i, file] of files.entries()) refs.push(await uploadPromptRef(file, names[i]!));
      await createJob.mutateAsync({
        data: {
          kind: 'PROMPT',
          source: trimmed,
          ...(refs.length > 0 ? { options: JSON.stringify({ refs }) } : {}),
        },
      });
      setPrompt('');
      setFiles([]);
      toast.success('Effect generation queued — the render worker picks it up shortly');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to queue the generation job');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Create Effect</h1>
        <span className="text-sm text-muted-foreground">
          {chains.length} effect{chains.length === 1 ? '' : 's'}
        </span>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
          Reload
        </Button>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-3 pt-6">
          <div className="flex items-center gap-1 self-start rounded-md border border-border p-1">
            <Button
              type="button"
              variant={mode === 'prompt' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setMode('prompt')}
              disabled={submitting}
            >
              <Sparkles className="size-4" />
              AI prompt
            </Button>
            <Button
              type="button"
              variant={mode === 'template' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setMode('template')}
              disabled={submitting}
            >
              <LayoutTemplate className="size-4" />
              Motion template
            </Button>
            <Button
              type="button"
              variant={mode === 'footage' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setMode('footage')}
              disabled={submitting}
            >
              <Film className="size-4" />
              My footage
            </Button>
          </div>
          {mode === 'template' ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">Template</span>
                  <select
                    className={SELECT_CLASS}
                    value={presetId}
                    onChange={(e) => selectPreset(e.target.value)}
                    disabled={submitting}
                  >
                    {TEMPLATE_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </label>
                {preset.chrome && (
                  <label className="flex flex-col gap-1 text-xs">
                    <span className="text-muted-foreground">Card style</span>
                    <select
                      className={SELECT_CLASS}
                      value={chrome}
                      onChange={(e) => setChrome(e.target.value)}
                      disabled={submitting}
                    >
                      {preset.chrome.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">Variant name (optional)</span>
                  <Input
                    value={variantName}
                    onChange={(e) => setVariantName(e.target.value.toLowerCase())}
                    placeholder={preset.id}
                    disabled={submitting}
                    className={nameValid ? 'h-9 w-56' : 'h-9 w-56 border-destructive'}
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">Preview content</span>
                  <select
                    className={SELECT_CLASS}
                    value={previewHint}
                    onChange={(e) => setPreviewHint(e.target.value)}
                    disabled={submitting}
                  >
                    {PREVIEW_HINTS.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <p className="text-xs text-muted-foreground">{preset.blurb}</p>
              {!nameValid && (
                <p className="text-xs text-destructive">
                  Variant name must be 2-31 characters of a-z, 0-9 or dashes.
                </p>
              )}
              <div className="flex flex-col gap-2 rounded-md border border-border p-3">
                {preset.params.map((p) => (
                  <label key={p.key} className="flex items-center gap-3 text-xs">
                    <span className="w-32 shrink-0">{p.label}</span>
                    <input
                      type="range"
                      min={p.min}
                      max={p.max}
                      step={0.01}
                      value={tuned[p.key] ?? p.def}
                      onChange={(e) => setTuned({ ...tuned, [p.key]: Number(e.target.value) })}
                      className="flex-1 accent-primary"
                      disabled={submitting}
                    />
                    <span className="w-10 text-right font-mono tabular-nums">
                      {(tuned[p.key] ?? p.def).toFixed(2)}
                    </span>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  The compiler owns the choreography — pick a base, tune it, and the worker renders
                  a preview and submits a draft for review.
                </span>
                <div className="flex-1" />
                <Button
                  size="sm"
                  disabled={submitting || !nameValid}
                  onClick={() => void onSubmitTemplate()}
                >
                  {submitting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <LayoutTemplate className="size-4" />
                  )}
                  Render variant
                </Button>
              </div>
            </div>
          ) : mode === 'footage' ? (
            <div className="flex flex-col gap-3">
              <p className="text-xs text-muted-foreground">
                Your own clip becomes the effect — the pixels users see are the pixels you
                upload, not an AI approximation of them. Best results: a short overlay on a
                black background (sparks, fire, light leaks, lightning). 0.5-15s, up to 1080p
                and 60fps.
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">Clip</span>
                  <input
                    type="file"
                    accept={FOOTAGE_ACCEPT}
                    disabled={submitting}
                    onChange={(e) => setClip(e.target.files?.[0] ?? null)}
                    className="text-sm"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">Name (effect id)</span>
                  <Input
                    className={footageName === '' || footageNameValid ? 'h-9 w-56' : 'h-9 w-56 border-destructive'}
                    placeholder="ice-storm"
                    value={footageName}
                    onChange={(e) => setFootageName(e.target.value)}
                    disabled={submitting}
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">Blend</span>
                  <select
                    className={SELECT_CLASS}
                    value={blend}
                    onChange={(e) => setBlend(e.target.value)}
                    disabled={submitting}
                  >
                    {FOOTAGE_BLENDS.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">Placement</span>
                  <select
                    className={SELECT_CLASS}
                    value={placement}
                    onChange={(e) => setPlacement(e.target.value)}
                    disabled={submitting}
                  >
                    {FOOTAGE_PLACEMENTS.map((pl) => (
                      <option key={pl.id} value={pl.id}>
                        {pl.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">Preview content</span>
                  <select
                    className={SELECT_CLASS}
                    value={previewHint}
                    onChange={(e) => setPreviewHint(e.target.value)}
                    disabled={submitting}
                  >
                    {PREVIEW_HINTS.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={loop}
                    onChange={(e) => setLoop(e.target.checked)}
                    disabled={submitting}
                  />
                  <span className="text-muted-foreground">
                    Loop (off = plays once, then leaves the video untouched)
                  </span>
                </label>
              </div>

              <label className="flex flex-col gap-1 text-xs">
                <span className="text-muted-foreground">
                  Effects to stack on the clip (AI-composed) — optional
                </span>
                <Textarea
                  rows={2}
                  maxLength={MAX_COMPOSE_LEN}
                  placeholder='e.g. "handheld shake + warm HDR grade", "glitch RGB split then vignette"'
                  value={composeText}
                  onChange={(e) => setComposeText(e.target.value)}
                  disabled={submitting}
                />
                <span className="text-muted-foreground">
                  Leave empty to publish the clip as-is. Filled, the model picks the effects and
                  their order around your clip — the clip itself, its blend, its placement and its
                  licence stay exactly as set above, and you can Refine the result afterwards.
                </span>
              </label>

              <div className="flex flex-col gap-2 rounded-md border border-border p-3">
                <span className="text-xs font-medium">Licence — required</span>
                <p className="text-xs text-muted-foreground">
                  This clip ships to a public CDN and into other people&apos;s videos, so it
                  cannot be published unattested. A reviewer sees exactly what you type here.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="flex flex-col gap-1 text-xs">
                    <span className="text-muted-foreground">SPDX / licence</span>
                    <Input
                      className="h-9 w-56"
                      maxLength={MAX_SPDX_LEN}
                      placeholder="CC0-1.0"
                      value={spdx}
                      onChange={(e) => setSpdx(e.target.value)}
                      disabled={submitting}
                    />
                  </label>
                </div>
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">
                    Provenance — who made it, where it came from, and when
                  </span>
                  <Textarea
                    rows={3}
                    maxLength={MAX_PROVENANCE_LEN}
                    placeholder="Shot by me on 2026-09-07 with an iPhone 15 Pro; no third-party material."
                    value={provenance}
                    onChange={(e) => setProvenance(e.target.value)}
                    disabled={submitting}
                  />
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={rightsOwned}
                    onChange={(e) => setRightsOwned(e.target.checked)}
                    disabled={submitting}
                  />
                  <span>I hold the rights to distribute this clip inside the TipMi app.</span>
                </label>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  onClick={() => void onSubmitFootage()}
                  disabled={submitting || !footageReady}
                >
                  {submitting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Film className="size-4" />
                  )}
                  Render footage effect
                </Button>
                {clip && <span className="text-xs text-muted-foreground">{clip.name}</span>}
              </div>
            </div>
          ) : (
            <>
              <Textarea
                rows={4}
                maxLength={MAX_PROMPT_LEN}
                placeholder='Describe the effect… e.g. "retro CRT screen with scanlines", "golden title that pops in", "glowing outline around the person"'
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={submitting}
              />
              {files.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {files.map((file, i) => (
                    <span
                      key={`${file.name}-${i}`}
                      className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs"
                    >
                      {file.name}
                      <button
                        type="button"
                        aria-label={`Remove ${file.name}`}
                        disabled={submitting}
                        onClick={() => setFiles(files.filter((_, j) => j !== i))}
                      >
                        <X className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT}
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={files.length >= MAX_REFS || submitting}
                >
                  <Paperclip className="size-4" />
                  Add reference
                </Button>
                <span className="text-xs text-muted-foreground">
                  Optional image/video style references (max {MAX_REFS}; videos are sampled into
                  stills)
                </span>
                <div className="flex-1" />
                <Button
                  size="sm"
                  disabled={submitting || !prompt.trim()}
                  onClick={() => void onSubmit()}
                >
                  {submitting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Sparkles className="size-4" />
                  )}
                  Generate
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {isError && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <div className="flex items-center gap-2">
            <AlertCircle className="size-4 shrink-0" />
            <span>{error instanceof Error ? error.message : 'Failed to load jobs.'}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      )}

      {isLoading && jobs.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-24">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      ) : chains.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-24 text-sm text-muted-foreground">
          No generation jobs yet. Describe an effect above and hit Generate.
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          {chains.map((chain) => (
            <EffectCard key={chain[0]!.id ?? -1} chain={chain} />
          ))}
        </div>
      )}
    </div>
  );
}
